import { useState } from "react";
import { Grid, Stack, Typography, IconButton, Button, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import QuizIcon from "../../../../../assets/icons/quiz-icon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { StyledPaper } from "../../../../../customComponents/styled/styledPaper";
import MathContent from "../../../../customComponents/MathContent";
import {
  LessonPlanLabel,
  LessonPlanValue,
} from "../../../../../customComponents/styled/StyledTypgraphies";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const ViewQuizAnswers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [isInstructionExpanded, setIsInstructionExpanded] = useState(false);
  const { quiz } = location.state || {};

  if (!quiz) {
    return (
      <Box>
        <Typography variant="h6">Quiz not found</Typography>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // quiz.answers contains the student's responses with full question+option data
  // including is_answer (via QuestionSchema, not the incognito schema)
  const answers = quiz.answers || [];

  let correctCount = 0;
  let wrongCount = 0;
  let skippedCount = 0;
  answers.forEach((a) => {
    const isSkipped = !a.option_id;
    if (isSkipped) {
      skippedCount++;
    } else {
      const chosen = a.question?.options?.find((o) => o.id === a.option_id);
      if (chosen?.is_answer) correctCount++;
      else wrongCount++;
    }
  });
  const totalAnswered = answers.length;
  const scorePercent = totalAnswered > 0 ? Math.round((correctCount / totalAnswered) * 100) : 0;
  const instructionText = quiz?.instruction?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || "";
  const hasInstruction = Boolean(instructionText);
  const isLongInstruction = instructionText.length > 100;

  return (
    <Box>
      <Stack gap={4}>
        {/* Back button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">Back</Typography>
        </Box>

        {/* Quiz info header */}
        <StyledPaper sx={{ py: 4, px: 8 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <img src={QuizIcon} style={{ height: "24px", width: "24px" }} alt="quiz" />
            <Typography
              sx={{
                color: "1E1E1E",
                fontSize: "23.813px",
                fontWeight: 700,
                textTransform: "capitalize",
              }}
            >
              {quiz?.subject} Quiz
            </Typography>
          </Stack>
          <Box mt={4} sx={{ display: "flex", flexWrap: "wrap", rowGap: 4, ">*": { width: "33%" } }}>
            <Stack>
              <LessonPlanLabel>Date</LessonPlanLabel>
              <LessonPlanValue>{formatDate(quiz?.updated_at)}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Subject</LessonPlanLabel>
              <LessonPlanValue>{quiz?.subject}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Class</LessonPlanLabel>
              <LessonPlanValue>{quiz?.level}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Questions</LessonPlanLabel>
              <LessonPlanValue>{quiz?.questions?.length}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Duration</LessonPlanLabel>
              <LessonPlanValue>{quiz?.duration} mins</LessonPlanValue>
            </Stack>
          </Box>
        </StyledPaper>

        {hasInstruction && (
          <StyledPaper sx={{ py: 3, px: 8 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>Instructions</Typography>
              {isLongInstruction && (
                <Button
                  size="small"
                  endIcon={isInstructionExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  onClick={() => setIsInstructionExpanded((current) => !current)}
                  sx={{ textTransform: "none", minWidth: "auto", px: 1 }}
                >
                  {isInstructionExpanded ? "Show less" : "Show more"}
                </Button>
              )}
            </Box>
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.7,
                display: "-webkit-box",
                WebkitLineClamp: isLongInstruction && !isInstructionExpanded ? 2 : "unset",
                WebkitBoxOrient: "vertical",
                overflow: isLongInstruction && !isInstructionExpanded ? "hidden" : "visible",
                "& p": { m: 0 },
                "& p + p": { mt: 1 },
                "& ul, & ol": { my: 0, pl: 3 },
              }}
            >
              <MathContent html={quiz.instruction} />
            </Typography>
          </StyledPaper>
        )}

        {/* Score summary */}
        <StyledPaper sx={{ py: 4, px: 8 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "18px", mb: 3 }}>Your Result</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            <Box sx={{ textAlign: "center", minWidth: "80px" }}>
              <Typography
                sx={{
                  fontSize: "40px",
                  fontWeight: 800,
                  color: scorePercent >= 50 ? "#27AE60" : "#E02424",
                  lineHeight: 1,
                }}
              >
                {scorePercent}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Score</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: isDark ? "#1b3a27" : "#F0FFF4",
                  px: 3,
                  py: 2,
                  borderRadius: "12px",
                }}
              >
                <CheckCircleIcon sx={{ color: "#27AE60" }} />
                <Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#27AE60", lineHeight: 1 }}>
                    {correctCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Correct</Typography>
                </Stack>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: isDark ? "#3a1b1b" : "#FFF5F5",
                  px: 3,
                  py: 2,
                  borderRadius: "12px",
                }}
              >
                <CancelIcon sx={{ color: "#E02424" }} />
                <Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#E02424", lineHeight: 1 }}>
                    {wrongCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Wrong</Typography>
                </Stack>
              </Box>
              {skippedCount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: isDark ? "#3a2e00" : "#FFFBEB",
                    px: 3,
                    py: 2,
                    borderRadius: "12px",
                  }}
                >
                  <RemoveCircleOutlineIcon sx={{ color: "#F59E0B" }} />
                  <Stack>
                    <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#F59E0B", lineHeight: 1 }}>
                      {skippedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Skipped</Typography>
                  </Stack>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: isDark ? "#2a2a2a" : "#F5F5F5",
                  px: 3,
                  py: 2,
                  borderRadius: "12px",
                }}
              >
                <Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: "22px", lineHeight: 1 }}>
                    {totalAnswered}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total</Typography>
                </Stack>
              </Box>
            </Box>
          </Box>
        </StyledPaper>

        {/* Per-question breakdown */}
        {answers.map((answer, i) => {
          const question = answer.question;
          const chosenOptionId = answer.option_id;
          const isSkipped = !chosenOptionId;
          const chosenOption = isSkipped ? null : question?.options?.find((o) => o.id === chosenOptionId);
          const isCorrect = !isSkipped && chosenOption?.is_answer === true;
          const borderAccent = isSkipped ? "#F59E0B" : isCorrect ? "#27AE60" : "#E02424";
          const badgeBg = isSkipped ? "#F59E0B" : isCorrect ? "#27AE60" : "#E02424";

          return (
            <StyledPaper
              sx={{
                py: 4,
                px: 8,
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                borderLeft: `4px solid ${borderAccent}`,
              }}
              key={answer.question_id}
            >
              <Box
                sx={{
                  bgcolor: badgeBg,
                  color: "white",
                  width: "fit-content",
                  px: "20px",
                  py: "15px",
                  borderRadius: "72px",
                  fontWeight: 700,
                  fontSize: "20px",
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Grid container rowSpacing={3} columnSpacing={4}>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Typography
                      sx={{ p: 1, fontSize: "16px", flex: 1 }}
                    >
                      <MathContent html={question?.body} />
                    </Typography>
                    {isSkipped ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, flexShrink: 0 }}>
                        <RemoveCircleOutlineIcon sx={{ color: "#F59E0B", fontSize: "1rem" }} />
                        <Typography variant="caption" sx={{ color: "#F59E0B", fontWeight: 600 }}>Not answered</Typography>
                      </Box>
                    ) : isCorrect ? (
                      <CheckCircleIcon sx={{ color: "#27AE60", mt: 1, flexShrink: 0 }} />
                    ) : (
                      <CancelIcon sx={{ color: "#E02424", mt: 1, flexShrink: 0 }} />
                    )}
                  </Box>
                </Grid>
                {question?.options?.map((option) => {
                  const isStudentChoice = option.id === chosenOptionId;
                  const isCorrectAnswer = option.is_answer === true;

                  let borderColor = isDark ? "#444" : "#E0E0E0";
                  let bgcolor = "transparent";
                  let color = isDark ? theme.palette.text.primary : "#000";
                  let fontWeight = 400;
                  let trailingIcon = null;

                  if (isStudentChoice && isCorrectAnswer) {
                    borderColor = "#27AE60";
                    bgcolor = isDark ? "#1b3a27" : "#F0FFF4";
                    color = "#27AE60";
                    fontWeight = 600;
                    trailingIcon = <CheckCircleIcon sx={{ color: "#27AE60", fontSize: "18px", flexShrink: 0 }} />;
                  } else if (isStudentChoice && !isCorrectAnswer) {
                    borderColor = "#E02424";
                    bgcolor = isDark ? "#3a1b1b" : "#FFF5F5";
                    color = "#E02424";
                    fontWeight = 600;
                    trailingIcon = <CancelIcon sx={{ color: "#E02424", fontSize: "18px", flexShrink: 0 }} />;
                  } else if (!isStudentChoice && isCorrectAnswer) {
                    borderColor = "#27AE60";
                    color = "#27AE60";
                    trailingIcon = <CheckCircleIcon sx={{ color: "#27AE60", fontSize: "18px", flexShrink: 0 }} />;
                  }

                  return (
                    <Grid key={option.id} item xs={12} md={6}>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor,
                          borderRadius: "14px",
                          padding: "17px",
                          fontWeight,
                          color,
                          bgcolor,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{ flex: 1 }}
                        >
                          <MathContent html={option.body} />
                        </Box>
                        {trailingIcon}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </StyledPaper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ViewQuizAnswers;
